import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  IdCard,
  Search,
  Download,
  RefreshCw,
  AlertTriangle,
  Info,
  ChevronRight,
  X,
  ShieldAlert
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { useFeatures } from '../../contexts/FeaturesContext';
import { Toast } from '../ui/Toast';
import EnTeteSection from './EnTeteSection';
import CarteReglages from './CarteReglages';
import ChargementSection from './ChargementSection';

/**
 * ============================================================================
 * QUI PORTE UN BADGE RFID
 * ============================================================================
 *
 * Jusqu'ici, savoir si un employé avait un badge demandait d'ouvrir sa fiche
 * dans Ressources humaines, une par une. Cet onglet lit la même donnée d'un
 * seul coup.
 *
 * ----------------------------------------------------------------------------
 * CETTE PAGE NE MODIFIE RIEN
 * ----------------------------------------------------------------------------
 * Pas de formulaire, pas d'enregistrement, aucun des deux régimes de
 * PARAMETRAGE-MODULES.md §5 : le badge s'attribue et se retire depuis la fiche
 * employé, qui reste le seul chemin d'écriture. Un appui sur une ligne y mène.
 *
 * Ne pas y ajouter de champ modifiable : le badge est unique en base, et deux
 * écrans qui l'écrivent, c'est le conflit qu'on découvre le matin où deux
 * personnes ne pointent plus.
 *
 * ----------------------------------------------------------------------------
 * ⚠️ LE BADGE TIENT EN DEUX COLONNES, PAS UNE
 * ----------------------------------------------------------------------------
 * `employes.rfid_uid` ET `employes.numero_badge`. Le kiosque identifie
 * quelqu'un par L'UNE OU L'AUTRE (voir /api/employes/verify-badge). Les deux
 * colonnes sont donc affichées SÉPARÉMENT, et une absence s'écrit en mots
 * (« Non attribué »), jamais en case vide : une case vide se lit comme une
 * donnée qu'on n'a pas chargée, pas comme un badge qui n'existe pas.
 *
 * ----------------------------------------------------------------------------
 * Pas de filtre « sans badge »
 * ----------------------------------------------------------------------------
 * Écarté explicitement à la commande. La question posée est « qui a un
 * badge », et la liste du personnel sans badge, c'est la liste du personnel :
 * elle vit dans Ressources humaines. Ne pas le rajouter sans le redemander.
 *
 * ----------------------------------------------------------------------------
 * Les deux informations qui valent l'écran
 * ----------------------------------------------------------------------------
 * 1. Un badge dont le dernier pointage est « jamais » est soit mal encodé,
 *    soit porté par quelqu'un qui ne pointe pas. C'est l'anomalie la plus
 *    fréquente, elle se voit en ambre.
 * 2. Un badge encore attribué à un employé RADIÉ est une anomalie de sécurité :
 *    la radiation PEUT libérer le badge (RadiationEmployeService) mais ce n'est
 *    qu'une option. Le statut est donc affiché, et un bandeau les compte.
 *
 * La norme de l'écran : PARAMETRAGE-MODULES.md (§3 en-tête, §4 carte,
 * §6 chargement/vide/échec, §8 encadré). Accent `#ae7467` : quatrième de la
 * palette d'onglets de la norme, les trois premiers accents de la page étant
 * déjà pris par les horaires fixes, variables et les groupes.
 */

const ACCENT = '#ae7467';
const ACCENT_SOMBRE = '#8d5a4f';

// Les libellés de statut. Repli sur la valeur brute : `employes.statut` a déjà
// gagné `radie` après coup, et une valeur inconnue doit s'afficher telle quelle
// plutôt que de disparaître derrière un libellé inventé.
const STATUTS = {
  actif: { libelle: 'Actif', classe: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  inactif: { libelle: 'Inactif', classe: 'bg-gray-100 text-gray-500 border-gray-200' },
  radie: { libelle: 'Radié', classe: 'bg-red-50 text-red-700 border-red-200' },
  conge: { libelle: 'Congé', classe: 'bg-amber-50 text-amber-700 border-amber-200' },
  suspendu: { libelle: 'Suspendu', classe: 'bg-red-50 text-red-700 border-red-200' },
  transfere: { libelle: 'Transféré', classe: 'bg-blue-50 text-blue-700 border-blue-200' }
};

const libelleStatut = (statut) => STATUTS[statut]?.libelle || statut || 'Inconnu';
const classeStatut = (statut) =>
  STATUTS[statut]?.classe || 'bg-gray-100 text-gray-500 border-gray-200';

/**
 * Un numéro de badge, ou son absence.
 *
 * `font-mono` parce qu'un UID RFID est une suite de caractères qu'on compare à
 * l'œil avec ce qui est gravé sur la carte : en police proportionnelle, un 0 et
 * un O ne se distinguent pas.
 */
const ValeurBadge = ({ valeur }) => {
  if (!valeur) {
    return <span className="text-xs italic text-gray-400">Non attribué</span>;
  }
  return (
    <span className="font-mono text-[13px] font-semibold tracking-tight text-gray-800">
      {valeur}
    </span>
  );
};

/**
 * Le dernier pointage, ou « Jamais ».
 *
 * La distinction est l'information la plus utile de l'écran : elle ne doit pas
 * se deviner. Une date reste en gris, « jamais » passe en ambre avec son icône.
 *
 * La chaîne arrive déjà formatée par le serveur (DATE_FORMAT en SQL). On ne la
 * repasse JAMAIS dans un `new Date()` pour la réafficher : un `Date` JS décale
 * d'un jour selon le fuseau, et cette facture a déjà été payée dans ce dépôt.
 * On se contente de séparer la date de l'heure sur l'espace.
 */
// La date d'attribution d'un badge. Même mise en forme que le dernier pointage,
// mais une absence s'y lit autrement : « Jamais pointé » est une alerte — le
// badge ne sert pas — tandis qu'une attribution inconnue est seulement une
// information que personne n'a enregistrée, et souvent parce que le badge est
// antérieur au 4 octobre 2026. D'où le gris discret ici, et l'ambre là-bas.
const DateAttribution = ({ valeur }) => {
  if (!valeur) {
    return <span className="text-xs italic text-gray-400">Non renseigné</span>;
  }

  const [datePart, heure] = String(valeur).split(' ');
  const [annee, mois, jour] = String(datePart).split('-');
  const dateFr = annee && mois && jour ? `${jour}/${mois}/${annee}` : datePart;

  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className="font-mono text-[13px] text-gray-700">{dateFr}</span>
      {heure && <span className="font-mono text-[11px] text-gray-400">{heure}</span>}
    </span>
  );
};

const DernierPointage = ({ valeur }) => {
  if (!valeur) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700">
        <AlertTriangle size={13} className="flex-shrink-0" />
        Jamais
      </span>
    );
  }

  // Le serveur envoie « AAAA-MM-JJ HH:MM » — une forme qui se trie et se compare,
  // et c'est pour cela qu'elle ne change pas côté serveur. L'écran, lui, écrit la
  // date comme on l'écrit ici : 04/10/2026.
  //
  // ⚠️ La conversion est faite par DÉCOUPAGE DE LA CHAÎNE, et jamais par
  // `new Date('2026-10-04')` : la norme impose de lire une date sans heure comme
  // de l'UTC, et un poste à l'ouest de Greenwich afficherait la veille. Le défaut
  // a déjà été payé deux fois sur ce chantier.
  const [datePart, heure] = String(valeur).split(' ');
  const [annee, mois, jour] = String(datePart).split('-');
  const dateFr = annee && mois && jour ? `${jour}/${mois}/${annee}` : datePart;

  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className="font-mono text-[13px] text-gray-700">{dateFr}</span>
      {heure && <span className="font-mono text-[11px] text-gray-400">{heure}</span>}
    </span>
  );
};

const BadgesRfid = () => {
  const navigate = useNavigate();
  const { estActive } = useFeatures();

  // La licence, côté écran. La carte « Règles de pointage » des Outils
  // administratifs est déjà filtrée sur `cap.rfid` (ParametrageApp.jsx,
  // LICENCE_PAR_SECTION) : on n'arrive donc ici que si l'école a les badges.
  // Ce second contrôle existe parce que la page porte désormais un onglet qui
  // appelle une route gardée par `cap.rfid` : si le filtre amont bougeait un
  // jour, mieux vaut une explication qu'un 403 nu dans la console.
  //
  // ⚠️ Ce masquage reste un confort d'affichage. Ce qui protège, c'est
  // `requireFeature('cap.rfid')` sur les deux routes (FeaturesContext le dit).
  const rfidOuvert = estActive('cap.rfid');

  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [employes, setEmployes] = useState([]);
  const [recherche, setRecherche] = useState('');
  const [exportEnCours, setExportEnCours] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const reponse = await api.employes.getBadgesRfid();
      if (reponse?.success) {
        setEmployes(Array.isArray(reponse.employes) ? reponse.employes : []);
      } else {
        // Une lecture qui échoue ne s'affiche jamais comme une liste vide
        // (PARAMETRAGE-MODULES.md §6) : « aucun badge attribué » et « on n'a
        // pas pu lire » sont deux réponses opposées, et l'une ferait croire
        // qu'il n'y a rien à voir.
        setEmployes([]);
        setErreur(reponse?.message || reponse?.error || 'Chargement impossible');
      }
    } catch (e) {
      setEmployes([]);
      setErreur(e.message || 'Chargement impossible');
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    if (!rfidOuvert) {
      setChargement(false);
      return;
    }
    charger();
  }, [charger, rfidOuvert]);

  // La recherche porte sur le nom, le prénom ET les deux numéros : on cherche
  // aussi bien « qui est Mabiala » que « à qui est ce badge-là », qui est le
  // geste du jour où l'on trouve une carte sur un bureau.
  const liste = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    if (!terme) return employes;
    return employes.filter(employe => {
      const champs = [
        employe.nom,
        employe.prenom,
        employe.fonction,
        employe.rfid_uid,
        employe.numero_badge
      ];
      return champs.some(champ => String(champ || '').toLowerCase().includes(terme));
    });
  }, [employes, recherche]);

  const radiesAvecBadge = useMemo(
    () => employes.filter(employe => employe.statut === 'radie').length,
    [employes]
  );
  const jamaisPointe = useMemo(
    () => employes.filter(employe => !employe.dernier_pointage).length,
    [employes]
  );

  const exporterPDF = async () => {
    setExportEnCours(true);
    const resultat = await api.employes.downloadBadgesRfidPDF();
    setExportEnCours(false);
    setToast({
      show: true,
      message: resultat?.success
        ? 'Liste des badges exportée.'
        : `Export impossible : ${resultat?.error || 'erreur inconnue'}`,
      type: resultat?.success ? 'success' : 'error'
    });
  };

  // ------------------------------------------------------------------
  // Licence fermée
  // ------------------------------------------------------------------
  if (!rfidOuvert) {
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-6">
        <div className="flex gap-3">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white">
            <ShieldAlert className="h-4 w-4 text-gray-400" />
          </div>
          <div className="text-xs leading-relaxed text-gray-600">
            <p className="mb-1 text-[13px] font-semibold text-gray-700">
              Les badges RFID ne sont pas inclus dans votre licence
            </p>
            <p>
              Cet onglet liste les porteurs de badge. Sans l&apos;extension « Badges RFID »,
              aucun badge n&apos;est attribuable et le kiosque de pointage reste fermé.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (chargement) {
    return <ChargementSection accent={ACCENT} libelle="Chargement des badges attribués…" />;
  }

  return (
    <div className="space-y-5">
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(prev => ({ ...prev, show: false }))}
      />

      <EnTeteSection
        icone={IdCard}
        titre="Badges RFID attribués"
        sousTitre="Qui porte un badge, et quand ce badge a servi pour la dernière fois"
        accent={ACCENT}
        accentSombre={ACCENT_SOMBRE}
        actions={(
          <>
            {/* Le bouton « Actualiser » est parti le 04/10/2026, à la demande de
                Daniel : la liste se recharge à chaque ouverture de l'onglet, et
                un badge ne s'attribue pas depuis cet écran — il n'y a donc rien
                à rafraîchir pendant qu'on le regarde. `RefreshCw` reste importé
                pour le bouton « Réessayer » de l'état d'erreur, qui, lui, sert. */}
            <button
              type="button"
              onClick={exporterPDF}
              disabled={exportEnCours || employes.length === 0}
              className="flex items-center gap-1.5 rounded-lg border border-transparent px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all hover:shadow-md disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
              style={exportEnCours || employes.length === 0 ? undefined : { backgroundColor: ACCENT }}
            >
              {exportEnCours
                ? <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                : <Download size={13} />}
              Exporter en PDF
            </button>
          </>
        )}
      />

      {/* Échec de lecture : bandeau dans la zone concernée, pas un toast — un
          toast disparaît et laisse un tableau vide sans explication
          (PARAMETRAGE-MODULES.md §6). */}
      {erreur && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-red-100">
            <AlertTriangle size={15} className="text-red-600" />
          </div>
          <p className="text-xs font-semibold text-red-700">
            Les badges n&apos;ont pas pu être chargés : {erreur}
          </p>
          <button
            type="button"
            onClick={charger}
            className="ml-auto flex items-center gap-1.5 rounded-lg bg-red-600 px-3.5 py-2 text-xs font-semibold text-white transition-all hover:bg-red-700"
          >
            <RefreshCw size={13} /> Réessayer
          </button>
        </div>
      )}

      {/* Encadré d'information — norme §8, un seul par section. */}
      <div className="rounded-xl border border-[#246990]/20 bg-[#eef7fb] p-4">
        <div className="flex gap-3">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-[#246990]/20 bg-white shadow-sm">
            <Info className="h-4 w-4 text-[#246990]" />
          </div>
          <div className="text-xs leading-relaxed text-gray-600">
            <p className="mb-1 text-[13px] font-semibold text-[#246990]">
              Ce que cette liste montre
            </p>
            <ul className="space-y-1">
              <li>
                • Un badge tient en <strong className="text-gray-700">deux numéros</strong> :
                l&apos;UID RFID, lu par le lecteur, et le numéro de badge, celui qui est
                imprimé sur la carte. Le kiosque accepte l&apos;un ou l&apos;autre, donc les
                deux sont affichés : un employé qui n&apos;en a qu&apos;un pointe quand même.
              </li>
              <li>
                • Un badge sans aucun pointage est soit mal encodé, soit porté par quelqu&apos;un
                qui ne pointe pas.
              </li>
              <li>
                • L&apos;attribution et le retrait se font dans
                {' '}<strong className="text-gray-700">Ressources humaines → fiche de
                l&apos;employé</strong>. Cette page est en lecture seule ; un clic sur une ligne
                y mène.
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* L'anomalie que cet écran doit faire ressortir : la radiation PEUT
          libérer le badge, elle ne le fait pas toujours. */}
      {radiesAvecBadge > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-red-100">
            <ShieldAlert size={15} className="text-red-600" />
          </div>
          <p className="text-xs leading-relaxed text-red-700">
            <strong className="font-semibold">
              {radiesAvecBadge} badge{radiesAvecBadge > 1 ? 's' : ''} encore attribué
              {radiesAvecBadge > 1 ? 's' : ''} à un employé radié.
            </strong>{' '}
            Le badge n&apos;est libéré que si la radiation l&apos;a demandé. Ouvrez la fiche
            pour le retirer.
          </p>
        </div>
      )}

      {/* Recherche : par nom, prénom, fonction ou numéro. Hors de la carte,
          pour qu'elle filtre visiblement ce qui est en dessous. */}
      {employes.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[260px] flex-1">
            <Search
              size={15}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              placeholder="Rechercher un nom, une fonction ou un numéro de badge…"
              aria-label="Rechercher un porteur de badge"
              className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-9 text-sm focus:border-transparent focus:ring-2"
              style={{ '--tw-ring-color': ACCENT }}
            />
            {recherche && (
              <button
                type="button"
                onClick={() => setRecherche('')}
                aria-label="Effacer la recherche"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 transition-colors hover:text-gray-600"
              >
                <X size={14} />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 text-xs text-gray-500">
            {jamaisPointe > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 font-semibold text-amber-700">
                <AlertTriangle size={12} />
                {jamaisPointe} jamais utilisé{jamaisPointe > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
      )}

      <CarteReglages
        titre="Personnel porteur d'un badge"
        accent={ACCENT}
        compteur={
          recherche.trim()
            ? `${liste.length} sur ${employes.length}`
            : `${employes.length} badge${employes.length > 1 ? 's' : ''}`
        }
        dense
      >
        {employes.length === 0 ? (
          // L'état vide dit OÙ s'attribue un badge : un tableau nu laisserait
          // croire que la fonctionnalité ne marche pas (norme §6).
          <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-gray-200 bg-gray-50">
              <IdCard className="h-5 w-5 text-gray-300" />
            </div>
            <p className="text-sm font-semibold text-gray-700">
              Aucun badge n&apos;est attribué pour le moment
            </p>
            <p className="max-w-md text-xs leading-relaxed text-gray-500">
              Un badge s&apos;attribue depuis <strong className="text-gray-600">Ressources
              humaines</strong>, sur la fiche de l&apos;employé : on y scanne la carte pour
              remplir l&apos;UID RFID, ou on saisit le numéro imprimé dessus. Dès qu&apos;un
              badge est lié, l&apos;employé apparaît ici.
            </p>
            <button
              type="button"
              onClick={() => navigate('/administration/ressources-humaines')}
              className="mt-1 flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-xs font-semibold text-gray-600 transition-all hover:border-gray-300 hover:bg-gray-50"
            >
              Ouvrir Ressources humaines <ChevronRight size={13} />
            </button>
          </div>
        ) : liste.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-14 text-center">
            <Search className="h-5 w-5 text-gray-300" />
            <p className="text-sm font-semibold text-gray-600">
              Aucun porteur de badge ne correspond à « {recherche.trim()} »
            </p>
            <p className="text-xs text-gray-400">
              La recherche porte sur le nom, le prénom, la fonction et les deux numéros.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/60">
                  <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wide text-gray-500">
                    Employé
                  </th>
                  <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-gray-500">
                    Fonction
                  </th>
                  <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-gray-500">
                    Statut
                  </th>
                  <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-gray-500">
                    UID RFID
                  </th>
                  <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-gray-500">
                    N° de badge
                  </th>
                  <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-gray-500">
                    Dernier pointage
                  </th>
                  <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-gray-500">
                    Attribué le
                  </th>
                  <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-gray-500">
                    Attribué par
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {liste.map(employe => {
                  const estRadie = employe.statut === 'radie';
                  return (
                    <tr
                      key={employe.id}
                      className={`group transition-colors ${
                        estRadie ? 'bg-red-50/40 hover:bg-red-50/70' : 'hover:bg-gray-50/70'
                      }`}
                    >
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <div
                            className="h-1.5 w-1.5 flex-shrink-0 rounded-full"
                            style={{ backgroundColor: estRadie ? '#dc2626' : ACCENT }}
                          />
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-gray-800">
                              {employe.nom} {employe.prenom}
                            </p>
                            {!employe.rfid_uid && (
                              <p className="text-[11px] text-gray-400">
                                Numéro de badge seul, sans UID
                              </p>
                            )}
                            {!employe.numero_badge && (
                              <p className="text-[11px] text-gray-400">
                                UID seul, sans numéro imprimé
                              </p>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3.5">
                        {employe.fonction
                          ? <span className="text-xs text-gray-600">{employe.fonction}</span>
                          : <span className="text-xs italic text-gray-400">Non renseignée</span>}
                      </td>

                      <td className="px-4 py-3.5">
                        <span
                          className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-semibold ${classeStatut(employe.statut)}`}
                        >
                          {estRadie && <ShieldAlert size={11} />}
                          {libelleStatut(employe.statut)}
                        </span>
                      </td>

                      <td className="px-4 py-3.5">
                        <ValeurBadge valeur={employe.rfid_uid} />
                      </td>

                      <td className="px-4 py-3.5">
                        <ValeurBadge valeur={employe.numero_badge} />
                      </td>

                      <td className="px-4 py-3.5">
                        <DernierPointage valeur={employe.dernier_pointage} />
                      </td>

                      {/* L'attribution n'est connue que depuis le 4 octobre 2026 :
                          les badges posés avant n'ont laissé aucune trace, et
                          leurs deux cases restent « Non renseigné ». Aucune date
                          n'est devinée — une date d'attribution fausse serait
                          pire qu'une absence, puisqu'on la croirait. */}
                      <td className="px-4 py-3.5">
                        <DateAttribution valeur={employe.attribue_le} />
                      </td>

                      <td className="px-4 py-3.5">
                        {employe.attribue_par ? (
                          <span className="text-[13px] text-gray-700">{employe.attribue_par}</span>
                        ) : (
                          <span className="text-xs italic text-gray-400">Non renseigné</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CarteReglages>
    </div>
  );
};

export default BadgesRfid;
